/* Warp & Skew — assets/warp-and-skew.js (Bearly Tools kit).
   One script, two tools. The section sets the mode on the root:
     <div id="ws-root" data-mode="text">   Warp & Skew Text  (sections/warp-and-skew.liquid, prefix ws)
     <div id="ws-root" data-mode="image">  Warp & Skew Image (sections/warp-and-skew-image.liquid, prefix wi)
   Tool name and event prefix come from the shell (.bt[data-bt] data-bt-name / data-bt-prefix,
   i.e. the Bearly Tool metaobject), so each page tracks as its own tool.
   Text mode needs opentype.js + clipper-lib; image mode needs clipper-lib only (loaded by the section)
   and the shared core (window.BT).
   Download popup + email gate: the ONE shared popup in the core (BT.runWithProgress):
   progress (this tool's stage texts), email step only if not unlocked, then the file.
   The core owns the popup, gate, Klaviyo subscribe, unlock keys and the <prefix>_email_* /
   <prefix>_progress_cancel / <prefix>_export_error events; <prefix>_download_click comes from
   BT.download when the file is saved. Old core cached (no runWithProgress): the file is saved directly. */
(() => {
"use strict";
const ROOT = document.getElementById("ws-root");
if(!ROOT) return;
const MODE = ROOT.dataset.mode === "image" ? "image" : "text";
const SHELL = ROOT.closest(".bt[data-bt]");
const TOOL_NAME = (SHELL && SHELL.dataset.btName) || (MODE === "image" ? "Warp & Skew Image" : "Warp & Skew Text");
const PREFIX = (SHELL && SHELL.dataset.btPrefix) || (MODE === "image" ? "wi" : "ws");
if(!window.ClipperLib || (MODE === "text" && !window.opentype)){ console.error("[" + TOOL_NAME + "] opentype.js or clipper-lib failed to load"); }
const track = (name, params) => { if(window.BT && window.BT.track) window.BT.track(name, params); else (window.dataLayer = window.dataLayer || []).push(Object.assign({event: PREFIX + "_" + name}, params || {})); };
const accentHex = () => {
  const v = getComputedStyle(ROOT).getPropertyValue("--bt-accent").trim();
  return /^#[0-9a-f]{6}$/i.test(v) ? v.toUpperCase() : /^#[0-9a-f]{3}$/i.test(v) ? ("#" + v.slice(1).split("").map(c => c + c).join("")).toUpperCase() : "#E8537A";
};

/* ---------------- fonts (text mode) ---------------- */
// Pinned to one google/fonts commit (files get renamed on main); every path below was checked at it.
const FONT_BASE = "https://cdn.jsdelivr.net/gh/google/fonts@23e54b51ddffbc7713c583748e3bd86f62b1fa4a/";
const FONT_BASE_2 = "https://raw.githubusercontent.com/google/fonts/23e54b51ddffbc7713c583748e3bd86f62b1fa4a/";
const FONTS = [
  ["Shrikhand","ofl/shrikhand/Shrikhand-Regular.ttf","groovy, heavy"],
  ["Cherry Bomb One","ofl/cherrybombone/CherryBombOne-Regular.ttf","bubbly, cute"],
  ["Luckiest Guy","apache/luckiestguy/LuckiestGuy-Regular.ttf","cartoon, bold"],
  ["Titan One","ofl/titanone/TitanOne-Regular.ttf","chunky, rounded"],
  ["Righteous","ofl/righteous/Righteous-Regular.ttf","retro, rounded"],
  ["Bowlby One SC","ofl/bowlbyonesc/BowlbyOneSC-Regular.ttf","heavy capitals"],
  ["Chewy","apache/chewy/Chewy-Regular.ttf","playful, bouncy"],
  ["Fredoka","ofl/fredoka/Fredoka%5Bwdth,wght%5D.ttf","soft, friendly"],
  ["Sniglet","ofl/sniglet/Sniglet-ExtraBold.ttf","rounded, kid-friendly"],
  ["Pacifico","ofl/pacifico/Pacifico-Regular.ttf","surf script"],
  ["Lobster","ofl/lobster/Lobster-Regular.ttf","vintage script"],
  ["Cookie","ofl/cookie/Cookie-Regular.ttf","light script"],
  ["Great Vibes","ofl/greatvibes/GreatVibes-Regular.ttf","wedding script, thin"],
  ["Permanent Marker","apache/permanentmarker/PermanentMarker-Regular.ttf","marker, handwritten"],
  ["Knewave","ofl/knewave/Knewave-Regular.ttf","brush, bold"],
  ["Abril Fatface","ofl/abrilfatface/AbrilFatface-Regular.ttf","fancy, bold"],
  ["Alfa Slab One","ofl/alfaslabone/AlfaSlabOne-Regular.ttf","chunky slab"],
  ["Ultra","apache/ultra/Ultra-Regular.ttf","western slab"],
  ["Bungee","ofl/bungee/Bungee-Regular.ttf","blocky signage"],
  ["Rubik Mono One","ofl/rubikmonoone/RubikMonoOne-Regular.ttf","wide, blocky"],
  ["Bebas Neue","ofl/bebasneue/BebasNeue-Regular.ttf","tall, clean"],
  ["Anton","ofl/anton/Anton-Regular.ttf","tall, heavy"],
  ["Staatliches","ofl/staatliches/Staatliches-Regular.ttf","condensed poster"],
  ["Poppins","ofl/poppins/Poppins-Bold.ttf","modern, clean"]
];
const fontCache = new Map();

/* ---------------- sample design (image mode) ---------------- */
// Shown on first load so the mat is never empty; replaced by the user's upload.
const SAMPLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 100">
<path fill="#E8537A" d="M50 88C20 64 5 47 5 31C5 16 17 9 28 9C38 9 46 15 50 23C54 15 62 9 72 9C83 9 95 16 95 31C95 47 80 64 50 88Z"/>
<polygon fill="#F2B84B" points="150,5 160.6,35.4 192.8,36.1 167.1,55.6 176.5,86.4 150,68 123.5,86.4 132.9,55.6 107.2,36.1 139.4,35.4"/>
<path fill="#2F8F6E" fill-rule="evenodd" d="M208 50A42 42 0 1 0 292 50A42 42 0 1 0 208 50ZM232 50A18 18 0 1 0 268 50A18 18 0 1 0 232 50Z"/>
<polygon fill="#2B6CB0" points="350,6 392,50 350,94 308,50"/>
<path fill="#7B5CD6" transform="translate(400 0)" d="M50 88C20 64 5 47 5 31C5 16 17 9 28 9C38 9 46 15 50 23C54 15 62 9 72 9C83 9 95 16 95 31C95 47 80 64 50 88Z"/>
</svg>`;

/* ---------------- presets ---------------- */
const PRESETS = [
  {id:"none",   name:"Straight",   type:"none",     amount:0},
  {id:"archUp", name:"Arch up",    type:"arc",      amount:.3,  lm:"follow"},
  {id:"archDn", name:"Arch down",  type:"arc",      amount:-.3, lm:"follow"},
  {id:"half",   name:"Half circle",type:"arc",      amount:.5,  lm:"follow"},
  {id:"circle", name:"Circle",     type:"arc",      amount:1,   lm:"follow"},
  {id:"rainbow",name:"Rainbow",    type:"arch",     amount:.5},
  {id:"smile",  name:"Smile",      type:"arch",     amount:-.5},
  {id:"bulge",  name:"Bulge",      type:"bulge",    amount:.55},
  {id:"pinch",  name:"Pinch",      type:"bulge",    amount:-.45},
  {id:"topUp",  name:"Top arc",    type:"arcUpper", amount:.6},
  {id:"topDn",  name:"Top dip",    type:"arcUpper", amount:-.45},
  {id:"botDn",  name:"Bottom arc", type:"arcLower", amount:.6},
  {id:"botUp",  name:"Bottom dip", type:"arcLower", amount:-.45},
  {id:"flag",   name:"Flag",       type:"flag",     amount:.45, freq:1},
  {id:"wave",   name:"Wave",       type:"wave",     amount:.5,  freq:1.5},
  {id:"ripple", name:"Ripple",     type:"flag",     amount:.3,  freq:2.5},
  {id:"rise",   name:"Rise",       type:"rise",     amount:.6},
  {id:"fish",   name:"Fish",       type:"fish",     amount:.55},
  {id:"perspL", name:"Grow left",  type:"persp",    amount:-.6},
  {id:"perspR", name:"Grow right", type:"persp",    amount:.6},
  {id:"tilt",   name:"Lean back",  type:"tilt",     amount:.55},
  {id:"taper",  name:"Taper ends", type:"taper",    amount:.6},
  {id:"barrel", name:"Barrel",     type:"barrel",   amount:.5},
  {id:"slant",  name:"Slant",      type:"slant",    amount:.45},
  {id:"env",    name:"Drag handles",type:"envelope",amount:0},
  {id:"path",   name:"Draw a line", type:"path",    amount:0, lm:"follow"}
];
const AMOUNT_LABEL = {arc:"Curve",arch:"Bend",bulge:"Bulge",arcUpper:"Bend",arcLower:"Bend",flag:"Height",wave:"Height",rise:"Rise",fish:"Bend",persp:"Depth",tilt:"Lean",taper:"Taper",barrel:"Swell",slant:"Slant"};
const START_PRESET = MODE === "image" ? "rainbow" : "bulge";

/* ---------------- state ---------------- */
const st = {
  source: MODE === "image" ? "svg" : "text", text:"Bearly\nArt", font:"Shrikhand", customFont:null,
  letter:0, line:.9, align:"center",
  preset:START_PRESET, type:"bulge", amount:.55, freq:1, lm:"stretch",
  env:null, path:null, pathPos:"on", pathStretch:false,
  width:8, weld:true, offset:0, join:"round", keepOrig:true, flipH:false,
  svgUnits:null, svgFrom:null, textColor: accentHex()
};
let svgBaseName = "design";

const $ = id => document.getElementById("ws-" + id);
const mat = $("mat");
const NS = "http://www.w3.org/2000/svg";
let srcCache = null;         // {units, minX,minY,W,H}
let view = null;             // {k, ox, oy, vb:[x,y,w,h]}
let dragging = null;
let lastOut = null;

/* ---------------- helpers ---------------- */
const clamp = (x,a,b) => Math.max(a, Math.min(b, x));
const S = u => 1 - (2*u-1)*(2*u-1);
function status(msg, ms){
  const el = $("status");
  if(!msg){ el.hidden = true; return; }
  el.textContent = msg; el.hidden = false;
  clearTimeout(status._t);
  if(ms) status._t = setTimeout(() => el.hidden = true, ms);
}

function crSeg(p0, p1, p2, p3, s){
  const s2 = s*s, s3 = s2*s;
  const f = k => 0.5*((2*p1[k]) + (-p0[k]+p2[k])*s + (2*p0[k]-5*p1[k]+4*p2[k]-p3[k])*s2 + (-p0[k]+3*p1[k]-3*p2[k]+p3[k])*s3);
  return [f(0), f(1)];
}
function crPoint(P, t){ // Catmull-Rom through points P at param t in [0, P.length-1]
  const n = P.length - 1;
  t = clamp(t, 0, n);
  const i = Math.min(n-1, Math.floor(t)), s = t - i;
  const p1 = P[i], p2 = P[i+1];
  const p0 = i > 0 ? P[i-1] : [2*p1[0]-p2[0], 2*p1[1]-p2[1]];
  const p3 = i+2 <= n ? P[i+2] : [2*p2[0]-p1[0], 2*p2[1]-p1[1]];
  return crSeg(p0, p1, p2, p3, s);
}
// envelope edge: points are [u, x, y] sorted by u; each dot keeps the u it was placed at
function envAt(P, u){
  const n = P.length; u = clamp(u, 0, 1);
  let i = 0; while(i < n-2 && u > P[i+1][0]) i++;
  const q = j => [P[j][1], P[j][2]];
  const p1 = q(i), p2 = q(i+1);
  const p0 = i > 0 ? q(i-1) : [2*p1[0]-p2[0], 2*p1[1]-p2[1]];
  const p3 = i+2 < n ? q(i+2) : [2*p2[0]-p1[0], 2*p2[1]-p1[1]];
  const s = (u - P[i][0]) / ((P[i+1][0] - P[i][0]) || 1e-9);
  return crSeg(p0, p1, p2, p3, clamp(s, 0, 1));
}

/* ---------------- warp math ---------------- */
function defaultEnv(){
  return {
    top: [0,1,2,3,4].map(i => [i/4, i/4 - .5, -.5]),
    bot: [0,1,2,3,4].map(i => [i/4, i/4 - .5,  .5]),
    left: [], right: []   // interior dots only: [v, x, y]; corners come from top/bottom ends
  };
}
// full edge as [param, x, y]; side edges borrow their corners from the top and bottom edges
function envFull(edge){
  const e = st.env;
  if(edge === "top" || edge === "bot") return e[edge];
  const T = e.top, B = e.bot, a = edge === "left" ? T[0] : T[T.length-1], b = edge === "left" ? B[0] : B[B.length-1];
  return [[0, a[1], a[2]], ...(e[edge] || []), [1, b[1], b[2]]];
}
let envSides = null;
function prepEnv(){ envSides = {L: envFull("left"), R: envFull("right")}; }
function defaultPath(){ return [[-.55,.12],[-.2,-.1],[.2,.1],[.55,-.12]]; }

let pathCache = null;
function buildPathCache(W){
  const P = st.path.map(p => [p[0]*W, p[1]*W]);
  const n = P.length - 1, per = 40, poly = [];
  for(let i=0;i<=n*per;i++) poly.push(crPoint(P, i/per));
  const cum = [0];
  for(let i=1;i<poly.length;i++) cum.push(cum[i-1] + Math.hypot(poly[i][0]-poly[i-1][0], poly[i][1]-poly[i-1][1]));
  pathCache = {poly, cum, per, L: cum[cum.length-1]};
}
function pathAt(t){
  const {poly, cum, L} = pathCache;
  let i;
  if(t <= 0) i = 0;
  else if(t >= L) i = poly.length - 2;
  else { let lo=0, hi=cum.length-1; while(hi-lo>1){ const m=(lo+hi)>>1; if(cum[m] <= t) lo=m; else hi=m; } i = lo; }
  const a = poly[i], b = poly[i+1], seg = (cum[i+1]-cum[i]) || 1e-9;
  const tx = (b[0]-a[0])/seg, ty = (b[1]-a[1])/seg;
  const s = t - cum[i];
  return [[a[0]+tx*s, a[1]+ty*s], [tx, ty]];
}

function warp(u, v, W, H){
  const a = st.amount, x0 = (u-.5)*W, y0 = (v-.5)*H, D = .25*W;
  const g = clamp(W/H*.35, .8, 2.5);
  switch(st.type){
    case "none": return [x0, y0];
    case "arc": {
      const th = Math.abs(a) * 2*Math.PI * .985;
      if(th < 1e-4) return [x0, y0];
      const R = W / th, phi = (u-.5)*th;
      if(a > 0){ const r = Math.max(0, R + (.5-v)*H); return [r*Math.sin(phi), R - r*Math.cos(phi)]; }
      const r = Math.max(0, R + (v-.5)*H); return [r*Math.sin(phi), r*Math.cos(phi) - R];
    }
    case "arch": return [x0, y0 - a*D*S(u)];
    case "bulge": return [x0, y0 * Math.max(.08, 1 + a*g*S(u))];
    case "arcUpper": return [x0, H/2 - (1-v)*H*Math.max(.08, 1 + a*g*S(u))];
    case "arcLower": return [x0, -H/2 + v*H*Math.max(.08, 1 + a*g*S(u))];
    case "flag": return [x0, y0 + a*H*.8*Math.sin(2*Math.PI*st.freq*u)];
    case "wave": { const p = 2*Math.PI*st.freq*u; return [x0, y0*Math.max(.1, 1 + .45*a*Math.cos(p)) + a*H*.7*Math.sin(p)]; }
    case "rise": return [x0, y0 - a*.15*W*Math.sin((u-.5)*Math.PI)];
    case "fish": return [x0, y0 * Math.max(.08, 1 + a*.9*Math.sin(2*Math.PI*u))];
    case "persp": return [x0, y0 * Math.max(.08, 1 + a*.85*(2*u-1))];
    case "tilt": return [x0 * Math.max(.08, 1 - a*.8*(1-2*v)), y0];
    case "taper": return [x0, y0 * Math.max(.08, 1 - a*.85*Math.pow(Math.abs(2*u-1), 1.3))];
    case "barrel": return [x0 * (1 + a*.35*S(v)), y0 * Math.max(.08, 1 + a*.5*S(u))];
    case "slant": return [x0 - a*.6*y0, y0];
    case "envelope": {
      // Coons patch: blends all four (possibly curved) edges
      const E = st.env, Tp = E.top, Bt = E.bot;
      const T = envAt(Tp, u), B = envAt(Bt, u), L = envAt(envSides.L, v), R = envAt(envSides.R, v);
      const tl = Tp[0], tr = Tp[Tp.length-1], bl = Bt[0], br = Bt[Bt.length-1];
      const f = (k, j) => (1-v)*T[k] + v*B[k] + (1-u)*L[k] + u*R[k]
        - ((1-u)*(1-v)*tl[j] + u*(1-v)*tr[j] + (1-u)*v*bl[j] + u*v*br[j]);
      return [f(0,1)*W, f(1,2)*H];
    }
    case "path": {
      const L = pathCache.L;
      const sc = st.pathStretch ? L/W : Math.min(1, L*.98/W);
      const [P, T] = pathAt(L/2 + (u-.5)*W*sc);
      const off = st.pathPos === "on" ? (.5 - v) : st.pathPos === "above" ? (1 - v) : -v;
      return [P[0] + T[1]*off*H*sc, P[1] - T[0]*off*H*sc];
    }
  }
  return [x0, y0];
}

/* ---------------- source geometry: text ---------------- */
function flattenCommands(cmds, seg){
  const out = []; let cur = null, x=0, y=0, sx=0, sy=0;
  const push = (px,py) => cur.push([px,py]);
  const line = (x1,y1) => {
    const n = Math.max(1, Math.ceil(Math.hypot(x1-x, y1-y)/seg));
    for(let i=1;i<=n;i++) push(x + (x1-x)*i/n, y + (y1-y)*i/n);
    x = x1; y = y1;
  };
  for(const c of cmds){
    if(c.type === "M"){ if(cur && cur.length > 2) out.push(cur); cur = [[c.x,c.y]]; x=sx=c.x; y=sy=c.y; }
    else if(c.type === "L") line(c.x, c.y);
    else if(c.type === "Q"){
      const len = Math.hypot(c.x1-x,c.y1-y) + Math.hypot(c.x-c.x1,c.y-c.y1);
      const n = clamp(Math.ceil(len/seg), 2, 200);
      for(let i=1;i<=n;i++){ const t=i/n, m=1-t; push(m*m*x+2*m*t*c.x1+t*t*c.x, m*m*y+2*m*t*c.y1+t*t*c.y); }
      x=c.x; y=c.y;
    } else if(c.type === "C"){
      const len = Math.hypot(c.x1-x,c.y1-y) + Math.hypot(c.x2-c.x1,c.y2-c.y1) + Math.hypot(c.x-c.x2,c.y-c.y2);
      const n = clamp(Math.ceil(len/seg), 2, 240);
      for(let i=1;i<=n;i++){ const t=i/n, m=1-t;
        push(m*m*m*x+3*m*m*t*c.x1+3*m*t*t*c.x2+t*t*t*c.x, m*m*m*y+3*m*m*t*c.y1+3*m*t*t*c.y2+t*t*t*c.y); }
      x=c.x; y=c.y;
    } else if(c.type === "Z"){
      if(cur){ if(Math.hypot(x-sx,y-sy) > 1e-6) line(sx, sy); cur.pop(); if(cur.length > 2) out.push(cur); cur = null; }
      x=sx; y=sy;
    }
  }
  if(cur && cur.length > 2) out.push(cur);
  return out;
}

function buildTextSource(font){
  const size = 100, sc = size / font.unitsPerEm;
  const asc = font.ascender*sc, desc = font.descender*sc;
  const lineH = (asc - desc) * st.line;
  const track = st.letter * size / 100;
  const lines = st.text.replace(/\r/g,"").split("\n");
  const units = [];
  const lineBoxes = [];
  let missing = 0;
  lines.forEach((ln, li) => {
    // characters the font doesn't have (emoji, other scripts) come back as glyph 0, the
    // "missing" box. Leave them out instead of cutting a row of rectangles.
    const glyphs = font.stringToGlyphs(ln).filter(g => { if(g.index === 0 && !/^\s$/.test(g.name || "")){ missing++; return false; } return true; });
    const items = []; let x = 0;
    glyphs.forEach((g, i) => {
      items.push({g, x});
      let adv = (g.advanceWidth || 0) * sc;
      if(i < glyphs.length-1) adv += font.getKerningValue(g, glyphs[i+1]) * sc;
      x += adv + (i < glyphs.length-1 ? track : 0);
    });
    const w = x;
    const off = st.align === "left" ? 0 : st.align === "right" ? -w : -w/2;
    const base = li * lineH;
    const lineUnits = [];
    for(const it of items){
      const cmds = it.g.getPath(it.x + off, base, size).commands;
      const contours = flattenCommands(cmds, 1.1);
      if(!contours.length) continue;
      let mnx=1e9,mxx=-1e9,mny=1e9,mxy=-1e9;
      for(const c of contours) for(const p of c){ if(p[0]<mnx)mnx=p[0]; if(p[0]>mxx)mxx=p[0]; if(p[1]<mny)mny=p[1]; if(p[1]>mxy)mxy=p[1]; }
      const u = {contours, cx:(mnx+mxx)/2, cy:0, line:li, box:[mnx,mny,mxx,mxy], fill:"nonzero"};
      units.push(u); lineUnits.push(u);
    }
    if(lineUnits.length){
      // line band: use cap-ish band so rigid letters share one centerline
      const top = base - asc*.72, bottom = base;
      lineUnits.forEach(u => u.cy = (top + bottom)/2);
      lineBoxes.push([top,bottom]);
    }
  });
  buildTextSource.missing = missing;
  return finishSource(units);
}

function finishSource(units){
  if(!units.length) return null;
  let minX=1e9,minY=1e9,maxX=-1e9,maxY=-1e9;
  for(const u of units) for(const c of u.contours) for(const p of c){
    if(p[0]<minX)minX=p[0]; if(p[0]>maxX)maxX=p[0]; if(p[1]<minY)minY=p[1]; if(p[1]>maxY)maxY=p[1];
  }
  return {units, minX, minY, W: Math.max(1e-6, maxX-minX), H: Math.max(1e-6, maxY-minY)};
}

/* ---------------- source geometry: SVG upload (image mode) ---------------- */
// Hand-rolled scanner (character codes only): no regex or string copy per number, so a path
// with 20,000 points parses in milliseconds, and store scripts that patch built-ins can't slow it.
function scanPath(d){
  d = String(d);
  let i = 0; const n = d.length; const out = [];
  const isD = c => c >= 48 && c <= 57;
  const ws = () => { while(i < n){ const c = d.charCodeAt(i); if(c === 32 || c === 44 || c === 9 || c === 10 || c === 13 || c === 12) i++; else break; } };
  const num = () => {
    ws(); let j = i, digits = false, c = d.charCodeAt(j);
    if(c === 43 || c === 45) j++;
    while(j < n && isD(d.charCodeAt(j))){ j++; digits = true; }
    if(d.charCodeAt(j) === 46){ j++; while(j < n && isD(d.charCodeAt(j))){ j++; digits = true; } }
    if(!digits) return null;
    c = d.charCodeAt(j);
    if(c === 101 || c === 69){ let k = j+1; const s = d.charCodeAt(k); if(s === 43 || s === 45) k++; if(k < n && isD(d.charCodeAt(k))){ while(k < n && isD(d.charCodeAt(k))) k++; j = k; } }
    const v = +d.substring(i, j); i = j; return isFinite(v) ? v : null;
  };
  const flag = () => { ws(); const c = d.charCodeAt(i); if(c === 48 || c === 49){ i++; return c - 48; } return null; };
  const COUNT = {M:2,L:2,H:1,V:1,C:6,S:4,Q:4,T:2,A:7,Z:0};
  let cmd = null;
  while(true){
    ws(); if(i >= n) break;
    const c = d.charCodeAt(i);
    if((c >= 65 && c <= 90) || (c >= 97 && c <= 122)){ cmd = d[i++]; }
    else if(cmd === null) break;
    const C = cmd.toUpperCase();
    const count = COUNT[C];
    if(count === undefined) break;
    if(C === "Z"){ out.push([cmd]); cmd = null; continue; }
    const args = [cmd];
    for(let k=0;k<count;k++){
      const v = (C === "A" && (k===3||k===4)) ? flag() : num();
      if(v === null) return out;
      args.push(v);
    }
    out.push(args);
    if(C === "M") cmd = cmd === "m" ? "l" : "L";
  }
  return out;
}
function splitSubpaths(d){
  const segs = scanPath(d); const subs = []; let cur = null;
  let x=0,y=0,sx=0,sy=0,lcx=null,lcy=null,lqx=null,lqy=null;
  for(const s of segs){
    const c = s[0], rel = c === c.toLowerCase() && c !== "z", C = c.toUpperCase();
    const ax = v => rel ? x+v : v, ay = v => rel ? y+v : v;
    let nlc = null, nlq = null;
    if(C === "M"){ if(cur) subs.push(cur); x = ax(s[1]); y = ay(s[2]); sx=x; sy=y; cur = `M${x} ${y}`; }
    else if(C === "Z"){ if(cur){ cur += "Z"; subs.push(cur); cur = null; } x=sx; y=sy; }
    else {
      if(!cur){ cur = `M${x} ${y}`; sx=x; sy=y; }
      if(C === "L"){ x=ax(s[1]); y=ay(s[2]); cur += `L${x} ${y}`; }
      else if(C === "H"){ x = rel ? x+s[1] : s[1]; cur += `L${x} ${y}`; }
      else if(C === "V"){ y = rel ? y+s[1] : s[1]; cur += `L${x} ${y}`; }
      else if(C === "C"){ const x1=ax(s[1]),y1=ay(s[2]),x2=ax(s[3]),y2=ay(s[4]),ex=ax(s[5]),ey=ay(s[6]); cur+=`C${x1} ${y1} ${x2} ${y2} ${ex} ${ey}`; nlc=[x2,y2]; x=ex;y=ey; }
      else if(C === "S"){ const x1 = lcx!==null ? 2*x-lcx : x, y1 = lcy!==null ? 2*y-lcy : y; const x2=ax(s[1]),y2=ay(s[2]),ex=ax(s[3]),ey=ay(s[4]); cur+=`C${x1} ${y1} ${x2} ${y2} ${ex} ${ey}`; nlc=[x2,y2]; x=ex;y=ey; }
      else if(C === "Q"){ const x1=ax(s[1]),y1=ay(s[2]),ex=ax(s[3]),ey=ay(s[4]); cur+=`Q${x1} ${y1} ${ex} ${ey}`; nlq=[x1,y1]; x=ex;y=ey; }
      else if(C === "T"){ const x1 = lqx!==null ? 2*x-lqx : x, y1 = lqy!==null ? 2*y-lqy : y; const ex=ax(s[1]),ey=ay(s[2]); cur+=`Q${x1} ${y1} ${ex} ${ey}`; nlq=[x1,y1]; x=ex;y=ey; }
      else if(C === "A"){ const ex=ax(s[6]),ey=ay(s[7]); cur+=`A${s[1]} ${s[2]} ${s[3]} ${s[4]} ${s[5]} ${ex} ${ey}`; x=ex;y=ey; }
    }
    [lcx,lcy] = nlc || [null,null]; [lqx,lqy] = nlq || [null,null];
  }
  if(cur) subs.push(cur);
  return subs;
}
function shapeToD(el){
  const t = el.tagName.toLowerCase(), g = a => parseFloat(el.getAttribute(a)) || 0;
  if(t === "path") return el.getAttribute("d") || "";
  if(t === "rect"){ const x=g("x"),y=g("y"),w=g("width"),h=g("height"); let rx=g("rx"),ry=g("ry"); if(!rx&&ry)rx=ry; if(!ry&&rx)ry=rx; rx=Math.min(rx,w/2); ry=Math.min(ry,h/2);
    if(!rx) return `M${x} ${y}H${x+w}V${y+h}H${x}Z`;
    return `M${x+rx} ${y}H${x+w-rx}A${rx} ${ry} 0 0 1 ${x+w} ${y+ry}V${y+h-ry}A${rx} ${ry} 0 0 1 ${x+w-rx} ${y+h}H${x+rx}A${rx} ${ry} 0 0 1 ${x} ${y+h-ry}V${y+ry}A${rx} ${ry} 0 0 1 ${x+rx} ${y}Z`; }
  if(t === "circle" || t === "ellipse"){ const cx=g("cx"),cy=g("cy"); const rx = t==="circle"?g("r"):g("rx"), ry = t==="circle"?g("r"):g("ry");
    return `M${cx-rx} ${cy}A${rx} ${ry} 0 1 0 ${cx+rx} ${cy}A${rx} ${ry} 0 1 0 ${cx-rx} ${cy}Z`; }
  if(t === "polygon" || t === "polyline"){ const p = (el.getAttribute("points")||"").trim().split(/[\s,]+/).map(Number); if(p.length < 6) return "";
    let d = `M${p[0]} ${p[1]}`; for(let i=2;i+1<p.length;i+=2) d += `L${p[i]} ${p[i+1]}`; return d + "Z"; }
  return "";
}
function cssToHex(c){
  const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(c || "");
  if(!m) return "#000000";
  return "#" + [m[1],m[2],m[3]].map(v => Math.round(+v).toString(16).padStart(2,"0")).join("");
}
// Gradient or pattern fill: use the first stop's color instead of black.
function paintColor(cs, svg){
  const m = /url\(\s*["']?#([^"')]+)/.exec(cs.fill || "");
  if(!m) return cssToHex(cs.fill);
  let ref; try{ ref = svg.querySelector("#" + CSS.escape(m[1])); }catch(e){}
  const stop = ref && ref.querySelector("stop");
  return stop ? cssToHex(stop.ownerDocument.defaultView.getComputedStyle(stop).stopColor) : "#000000";
}
// Strip anything that could run code or reach the network before the file touches the page.
function sanitizeSvg(root){
  root.querySelectorAll("script,foreignObject,iframe,embed,object,image,video,audio,animate,animateMotion,animateTransform,set,discard,handler,listener")
    .forEach(n => n.remove());
  for(const el of [root, ...root.querySelectorAll("*")]){
    for(const a of [...el.attributes]){
      const n = a.name.toLowerCase(), v = a.value.trim();
      if(n.startsWith("on")) el.removeAttribute(a.name);
      else if((a.localName === "href" || n === "src") && !v.startsWith("#")) el.removeAttribute(a.name);
      else if(/url\(\s*(?!["']?#)/i.test(v)) el.setAttribute(a.name, v.replace(/url\(\s*(?!["']?#)[^)]*\)/gi, "none"));
    }
  }
  root.querySelectorAll("style").forEach(s => { s.textContent = s.textContent.replace(/@import[^;]*;?/gi, "").replace(/url\(\s*(?!["']?#)[^)]*\)/gi, "none"); });
}
// Flatten one absolute subpath (output of splitSubpaths) to points. Pure math, no DOM, so a
// file with thousands of shapes reads in well under a second (getPointAtLength took minutes).
function flattenSub(sub, step){
  const pts = []; let x = 0, y = 0;
  const curveN = len => clamp(Math.ceil(len/step), 2, 240);
  for(const s of scanPath(sub)){
    const C = s[0];
    if(C === "M"){ x = s[1]; y = s[2]; pts.push([x,y]); }
    else if(C === "L"){ x = s[1]; y = s[2]; pts.push([x,y]); }
    else if(C === "C"){
      const [,x1,y1,x2,y2,ex,ey] = s, n = curveN(Math.hypot(x1-x,y1-y) + Math.hypot(x2-x1,y2-y1) + Math.hypot(ex-x2,ey-y2));
      for(let i=1;i<=n;i++){ const t=i/n, m=1-t; pts.push([m*m*m*x+3*m*m*t*x1+3*m*t*t*x2+t*t*t*ex, m*m*m*y+3*m*m*t*y1+3*m*t*t*y2+t*t*t*ey]); }
      x = ex; y = ey;
    } else if(C === "Q"){
      const [,x1,y1,ex,ey] = s, n = curveN(Math.hypot(x1-x,y1-y) + Math.hypot(ex-x1,ey-y1));
      for(let i=1;i<=n;i++){ const t=i/n, m=1-t; pts.push([m*m*x+2*m*t*x1+t*t*ex, m*m*y+2*m*t*y1+t*t*ey]); }
      x = ex; y = ey;
    } else if(C === "A"){
      let [,rx,ry,phi,fa,fs,ex,ey] = s;
      rx = Math.abs(rx); ry = Math.abs(ry);
      if(!rx || !ry || (ex === x && ey === y)){ if(ex !== x || ey !== y) pts.push([ex,ey]); x = ex; y = ey; continue; }
      const r = phi*Math.PI/180, co = Math.cos(r), si = Math.sin(r);
      const dx = (x-ex)/2, dy = (y-ey)/2, x1p = co*dx + si*dy, y1p = -si*dx + co*dy;
      const lam = x1p*x1p/(rx*rx) + y1p*y1p/(ry*ry);
      if(lam > 1){ const k = Math.sqrt(lam); rx *= k; ry *= k; }
      const num = rx*rx*ry*ry - rx*rx*y1p*y1p - ry*ry*x1p*x1p, den = rx*rx*y1p*y1p + ry*ry*x1p*x1p;
      const f = (fa === fs ? -1 : 1) * Math.sqrt(Math.max(0, num/den));
      const cxp = f*rx*y1p/ry, cyp = -f*ry*x1p/rx;
      const cx = co*cxp - si*cyp + (x+ex)/2, cy = si*cxp + co*cyp + (y+ey)/2;
      const ang = (ux,uy,vx,vy) => Math.atan2(ux*vy - uy*vx, ux*vx + uy*vy);
      const t1 = ang(1, 0, (x1p-cxp)/rx, (y1p-cyp)/ry);
      let dt = ang((x1p-cxp)/rx, (y1p-cyp)/ry, (-x1p-cxp)/rx, (-y1p-cyp)/ry);
      if(!fs && dt > 0) dt -= 2*Math.PI; else if(fs && dt < 0) dt += 2*Math.PI;
      const n = clamp(Math.ceil(Math.abs(dt)*Math.max(rx,ry)/step), 4, 720);
      for(let i=1;i<=n;i++){ const t = t1 + dt*i/n, c = Math.cos(t), sn = Math.sin(t); pts.push([cx + rx*c*co - ry*sn*si, cy + rx*c*si + ry*sn*co]); }
      x = ex; y = ey;
    }
  }
  if(pts.length > 1){ const a = pts[0], b = pts[pts.length-1]; if(Math.abs(a[0]-b[0]) < 1e-9 && Math.abs(a[1]-b[1]) < 1e-9) pts.pop(); }
  return pts;
}
// Files are measured inside a hidden blank frame: the store's own scripts don't see (or react to)
// thousands of shapes appearing, and nothing from the file can touch the real page.
let measureFrame = null;
function measureDoc(){
  if(!measureFrame || !measureFrame.isConnected || !measureFrame.contentDocument || !measureFrame.contentDocument.body){
    measureFrame = document.createElement("iframe");
    measureFrame.setAttribute("aria-hidden", "true"); measureFrame.tabIndex = -1; measureFrame.title = "";
    measureFrame.style.cssText = "position:absolute;left:-10000px;top:0;width:10px;height:10px;border:0;opacity:0;pointer-events:none";
    document.body.appendChild(measureFrame);
  }
  return measureFrame.contentDocument;
}
const ringArea = c => { let a = 0; for(let i=0, j=c.length-1; i<c.length; j=i++) a += (c[j][0]+c[i][0]) * (c[j][1]-c[i][1]); return a/2; };
function loadSvgText(txt){
  // DOCTYPE dropped first: no entity expansion, no external entity fetches.
  txt = window.BT && BT.cleanSvgText ? BT.cleanSvgText(txt) : String(txt).replace(/<!DOCTYPE[^[>]*(\[[\s\S]*?\])?\s*>/i, "")
    .replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-f]+);)[\w.:-]+;/gi, "");   // entities it declared are gone too
  const doc = new DOMParser().parseFromString(txt, "image/svg+xml");
  const root = doc.documentElement;
  if(!root || root.nodeName.toLowerCase() !== "svg" || doc.querySelector("parsererror")) throw new Error("That file isn't an SVG, or it's damaged. Try saving it again as SVG.");
  const skipped = {text: root.querySelectorAll("text").length, linked: root.querySelectorAll("use").length, images: root.querySelectorAll("image").length, outlines: 0, hidden: 0};
  if(window.BT && BT.sanitizeSvg) BT.sanitizeSvg(root); else sanitizeSvg(root);   // shared cleaner in the core; own copy if an older core is cached
  const mdoc = measureDoc(), getComputedStyle = el => mdoc.defaultView.getComputedStyle(el);
  const host = mdoc.createElement("div");
  host.style.cssText = "position:absolute;left:0;top:0;width:10px;height:10px;overflow:hidden";
  mdoc.body.appendChild(host);
  const svg = mdoc.importNode(root, true);
  host.appendChild(svg);
  try{
    const els = [...svg.querySelectorAll("path,rect,circle,ellipse,polygon,polyline")]
      .filter(el => !el.closest("defs,clipPath,mask,pattern,marker,symbol"));
    const tmp = mdoc.createElementNS(NS, "g");
    svg.appendChild(tmp);
    const tmpM = tmp.getCTM();
    const tmpI = tmpM ? tmpM.inverse() : null;
    let bb; try{ bb = svg.getBBox(); }catch(e){ bb = {width:100,height:100}; }
    const span = Math.max(bb.width, bb.height) || 100;
    const step = span / 700;
    const minArea = span*span*1e-9;
    const isHidden = el => {
      let op = 1;
      for(let a = el; a && a !== host; a = a.parentElement){
        const s = getComputedStyle(a);
        if(s.display === "none") return true;
        op *= parseFloat(s.opacity);
      }
      const s = getComputedStyle(el);
      return op < .01 || s.visibility === "hidden" || s.visibility === "collapse" || parseFloat(s.fillOpacity) < .01;
    };
    const units = [];
    for(const el of els){
      const cs = getComputedStyle(el);
      if(cs.fill === "none"){ if(cs.stroke !== "none") skipped.outlines++; continue; }
      if(isHidden(el)){ skipped.hidden++; continue; }
      const d = shapeToD(el); if(!d) continue;
      const elM = el.getCTM();
      const rel = (tmpI && elM) ? tmpI.multiply(elM) : new DOMMatrix();
      const scale = Math.sqrt(Math.abs(rel.a*rel.d - rel.b*rel.c)) || 1;
      const contours = [];
      for(const sub of splitSubpaths(d)){
        const raw = flattenSub(sub, step/scale);
        if(raw.length < 3) continue;
        const pts = raw.map(p => [rel.a*p[0] + rel.c*p[1] + rel.e, rel.b*p[0] + rel.d*p[1] + rel.f]);
        if(pts.some(p => !isFinite(p[0]) || !isFinite(p[1]))) continue;
        if(Math.abs(ringArea(pts)) <= minArea) continue;      // zero-area (a line, a dot): nothing to cut
        contours.push(pts);
      }
      if(!contours.length) continue;
      let mnx=1e9,mxx=-1e9,mny=1e9,mxy=-1e9;
      for(const c of contours) for(const p of c){ if(p[0]<mnx)mnx=p[0]; if(p[0]>mxx)mxx=p[0]; if(p[1]<mny)mny=p[1]; if(p[1]>mxy)mxy=p[1]; }
      units.push({contours, cx:(mnx+mxx)/2, cy:(mny+mxy)/2, box:[mnx,mny,mxx,mxy], fill: cs.fillRule === "evenodd" ? "evenodd" : "nonzero", color: paintColor(cs, svg)});
    }
    units.skipped = skipped;
    return units;
  } finally { host.remove(); }
}

/* ---------------- densify (for SVG sources with long straight runs) ---------------- */
function densify(contours, seg){
  return contours.map(c => {
    const out = [];
    for(let i=0;i<c.length;i++){
      const a = c[i], b = c[(i+1)%c.length]; out.push(a);
      const n = Math.floor(Math.hypot(b[0]-a[0], b[1]-a[1]) / seg);
      for(let k=1;k<n;k++) out.push([a[0]+(b[0]-a[0])*k/n, a[1]+(b[1]-a[1])*k/n]);
    }
    return out;
  });
}

/* ---------------- apply warp ---------------- */
function warpSource(src){
  const {units, minX, minY, W, H} = src;
  if(st.type === "path") buildPathCache(W);
  if(st.type === "envelope") prepEnv();
  const lm = st.lm;
  const out = [];
  for(const un of units){
    let contours;
    if(lm === "stretch" || st.type === "none"){
      contours = un.contours.map(c => c.map(p => warp((p[0]-minX)/W, (p[1]-minY)/H, W, H)));
    } else {
      const uc = (un.cx-minX)/W, vc = (un.cy-minY)/H;
      const P = warp(uc, vc, W, H);
      let cs = 1, sn = 0;
      if(lm === "follow"){
        const e = .003, A = warp(uc-e, vc, W, H), B = warp(uc+e, vc, W, H);
        const ang = Math.atan2(B[1]-A[1], B[0]-A[0]); cs = Math.cos(ang); sn = Math.sin(ang);
      }
      contours = un.contours.map(c => c.map(p => { const dx=p[0]-un.cx, dy=p[1]-un.cy; return [P[0]+dx*cs-dy*sn, P[1]+dx*sn+dy*cs]; }));
    }
    out.push({contours, fill: un.fill, color: un.color || null});
  }
  return out;
}

/* ---------------- simplify + clipper ---------------- */
function rdp(pts, tol){
  if(pts.length < 4) return pts;
  const keep = new Uint8Array(pts.length); keep[0] = keep[pts.length-1] = 1;
  const stack = [[0, pts.length-1]];
  while(stack.length){
    const [a, b] = stack.pop(); let md = 0, mi = -1;
    const ax=pts[a][0], ay=pts[a][1], bx=pts[b][0], by=pts[b][1], dx=bx-ax, dy=by-ay, L=Math.hypot(dx,dy)||1e-12;
    for(let i=a+1;i<b;i++){ const d = Math.abs(dy*pts[i][0] - dx*pts[i][1] + bx*ay - by*ax)/L; if(d>md){md=d;mi=i;} }
    if(md > tol && mi > 0){ keep[mi] = 1; stack.push([a,mi],[mi,b]); }
  }
  return pts.filter((_,i) => keep[i]);
}
function simplifyRing(c, tol){
  const r = rdp([...c, c[0]], tol); r.pop(); return r.length >= 3 ? r : c;
}
const SC = 10000;
function toClip(contours){ return contours.map(c => c.map(p => ({X: Math.round(p[0]*SC), Y: Math.round(p[1]*SC)}))); }
function union(paths, fillType){
  const c = new ClipperLib.Clipper();
  c.AddPaths(paths, ClipperLib.PolyType.ptSubject, true);
  const out = new ClipperLib.Paths();
  c.Execute(ClipperLib.ClipType.ctUnion, out, fillType, fillType);
  return out;
}
// Union in batches of 128: when hundreds of pieces pile on top of each other (Tilt on a
// circle, say) one big union takes seconds; batches give the same shape in a fraction of that.
// Welding has a time budget: past it, remaining batches stay as separate (still correct, just
// unwelded) pieces and the HUD says so, instead of freezing on a wildly detailed file.
const WELD_BUDGET_MS = 4000;
let weldDeadline = Infinity, weldPartial = false;
function unionMany(paths){
  if(paths.length <= 200) return union(paths, ClipperLib.PolyFillType.pftNonZero);
  const out = [];
  for(let i=0;i<paths.length;i+=128){
    if(performance.now() > weldDeadline){ weldPartial = true; out.push(...paths.slice(i)); return out; }
    out.push(...union(paths.slice(i, i+128), ClipperLib.PolyFillType.pftNonZero));
  }
  if(performance.now() > weldDeadline){ weldPartial = true; return out; }
  return out.length < paths.length*.9 ? unionMany(out) : union(out, ClipperLib.PolyFillType.pftNonZero);
}
// Offset input is simplified until it fits a point budget (details far smaller than the offset
// vanish inside it anyway). A piece is never dropped: if it simplifies away, its box is used.
const OFFSET_MAX_POINTS = 8000;
let offsetNote = "";
function simplifyForOffset(paths, tol){
  const t = tol*SC, out = [];
  for(const p of paths){
    const pts = p.map(q => [q.X, q.Y]);
    let r = rdp([...pts, pts[0]], t); r.pop();
    if(r.length < 3){
      let a=Infinity,b=Infinity,c=-Infinity,d=-Infinity; for(const q of pts){ if(q[0]<a)a=q[0]; if(q[1]<b)b=q[1]; if(q[0]>c)c=q[0]; if(q[1]>d)d=q[1]; }
      r = [[a,b],[c,b],[c,d],[a,d]];
      if(!ClipperLib.Clipper.Orientation(p)) r.reverse();
    }
    out.push(r.map(q => ({X:q[0], Y:q[1]})));
  }
  return out;
}
function offsetPaths(paths, dist, join, needsWeld){
  offsetNote = "";
  const count = ps => { let n = 0; for(const q of ps) n += q.length; return n; };
  // every piece keeps at least 3 points, so too many pieces can never fit: say so straight away
  if(paths.length*3 > OFFSET_MAX_POINTS){ offsetNote = "too detailed"; return null; }
  let tol = .002, input = simplifyForOffset(paths, tol);
  while(count(input) > OFFSET_MAX_POINTS && tol < dist/2){ tol *= 2; input = simplifyForOffset(paths, tol); }
  if(count(input) > OFFSET_MAX_POINTS){ offsetNote = "too detailed"; return null; }
  if(tol > .008) offsetNote = "simplified";
  // weld the (now light) pieces first: much cheaper than offsetting overlapping pieces
  if(needsWeld) input = unionMany(input);
  // ClipperOffset merges overlapping pieces itself, so no separate weld pass is needed first
  const co = new ClipperLib.ClipperOffset(2, Math.max(.0015, dist/60)*SC);
  co.AddPaths(paths, join === "round" ? ClipperLib.JoinType.jtRound : ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
  const out = new ClipperLib.Paths();
  co.Execute(out, dist*SC);
  return out;
}
function clipToD(paths, dx, dy){
  let s = "";
  for(const p of paths){
    if(p.length < 3) continue;
    s += "M" + p.map(q => fmt(q.X/SC - dx) + " " + fmt(q.Y/SC - dy)).join("L") + "Z";
  }
  return s;
}
const fmt = x => (Math.round(x*10000)/10000).toString();

/* ---------------- main compute ---------------- */
const fetchTTF = url => fetch(url).then(r => { if(!r.ok) throw new Error(r.status); return r.arrayBuffer(); });
async function getFont(name){
  if(name === "__custom" && st.customFont) return st.customFont;
  if(fontCache.has(name)) return fontCache.get(name);
  const f = FONTS.find(x => x[0] === name);
  status("Loading " + f[0] + "…");
  // jsDelivr first, then the same pinned file straight from GitHub. (Google's css2 only serves
  // woff2 to browsers, which opentype.js can't read, so there is no css2 fallback here.)
  const buf = await fetchTTF(FONT_BASE + f[1]).catch(() => fetchTTF(FONT_BASE_2 + f[1]));
  const font = opentype.parse(buf);
  fontCache.set(name, font);
  if(st.font === name || st.font === "__custom" || fontCache.has(st.font)) status("");   // nothing else still loading
  return font;
}

let lastGoodFont = st.font;   // last font that loaded; restored if a pick fails
async function rebuildSource(){
  const want = st.font;
  srcCache = null;
  try{
    if(st.source === "text"){
      if(!st.text.trim()){ render(); return; }
      let font;
      try{ font = await getFont(want); }
      catch(e){
        if(st.font !== want) return;   // a newer pick/rebuild owns the view
        console.error(e);
        const name = want === "__custom" ? st.customFontName : want;
        if(want !== lastGoodFont && (lastGoodFont === "__custom" ? st.customFont : fontCache.has(lastGoodFont))){
          st.font = lastGoodFont; drawFontList();
          status("Couldn't load " + name + " right now, so we kept " + (lastGoodFont === "__custom" ? st.customFontName : lastGoodFont) + ". Check your connection, or upload a .ttf or .otf file.", 7000);
          track("font_load_error", {font_name: want});
          rebuildSource();   // previous font is cached, so this is instant
          return;
        }
        throw e;
      }
      if(st.font !== want) return;     // stale: a newer font was picked meanwhile
      lastGoodFont = want;
      srcCache = buildTextSource(font);
      const miss = buildTextSource.missing;
      if(!miss && /left out\. Emoji/.test($("status").textContent)) status("");
      if(miss) status(`${miss === 1 ? "1 character isn't" : miss + " characters aren't"} in ${want === "__custom" ? st.customFontName : want}, so ${miss === 1 ? "it was" : "they were"} left out. Emoji and some languages need a different font.`, 6000);
    } else {
      if(st.svgUnits && st.svgUnits.length){
        const s = finishSource(st.svgUnits.map(u => ({...u})));
        const seg = Math.max(s.W, s.H) / 500;
        s.units = s.units.map(u => ({...u, contours: densify(u.contours, seg)}));
        srcCache = s;
      }
    }
  }catch(e){
    console.error(e);
    status(st.source === "text" ? "That font didn't load. Check your connection, or upload a .ttf or .otf file." : "Couldn't read that SVG.", 6000);
  }
  render();
}

function computeView(warped){
  let minX=1e9,minY=1e9,maxX=-1e9,maxY=-1e9;
  for(const u of warped) for(const c of u.contours) for(const p of c){
    if(p[0]<minX)minX=p[0]; if(p[0]>maxX)maxX=p[0]; if(p[1]<minY)minY=p[1]; if(p[1]>maxY)maxY=p[1];
  }
  // never let a design squashed to a sliver blow the zoom up (keeps the handles on screen)
  const w = Math.max(srcCache ? srcCache.W*1e-2 : 1e-6, maxX-minX);
  const k = st.width / w;
  const hIn = (maxY-minY)*k;
  const pad = Math.max(.6, Math.max(st.width, hIn)*.14) + st.offset;
  return {k, ox:minX, ox2:maxX, oy:minY, wIn:st.width, hIn, vb:[-pad, -pad, st.width + 2*pad, hIn + 2*pad]};
}

function viewMap(){
  const {k, ox, ox2, oy} = view, flip = st.flipH;
  return {
    toIn: (x, y) => [(flip ? ox2 - x : x - ox)*k, (y - oy)*k],
    fromIn: (X, Y) => [flip ? ox2 - X/k : X/k + ox, Y/k + oy]
  };
}
/* render scheduling: sliders and buttons ask for a render; light designs redraw on the next
   frame, heavy ones (last render > 200 ms) wait until the input pauses so the page never locks up */
let rqRaf = 0, rqT = 0, lastRenderMs = 0, busyShown = false;
function requestRender(){
  if(lastRenderMs > 200){
    cancelAnimationFrame(rqRaf); rqRaf = 0; clearTimeout(rqT);
    if(lastRenderMs > 900 && !busyShown){ busyShown = true; status("Working on it…"); }
    rqT = setTimeout(flushRender, 160);
    return;
  }
  if(!rqRaf) rqRaf = requestAnimationFrame(flushRender);
}
function flushRender(){
  cancelAnimationFrame(rqRaf); clearTimeout(rqT); rqRaf = rqT = 0;
  render();
  if(busyShown){ busyShown = false; if($("status").textContent === "Working on it…") status(""); }
}
const renderPending = () => !!(rqRaf || rqT);
// Each frame is built off-page and swapped in with ONE DOM change. Other scripts on the store
// watch the page for changes; thousands of single inserts made them rescan thousands of times.
let frame = null;
const put = node => frame.appendChild(node);
function render(){
  const t0 = performance.now();
  frame = document.createElementNS(NS, "g");   // one wrapper: the page sees a single new node
  try{ renderNow(); }
  finally{ mat.replaceChildren(frame); frame = null; if(!dragging) lastRenderMs = performance.now() - t0; }
}
function renderNow(){
  $("download").disabled = true;
  lastOut = null;
  if(!srcCache){
    drawMat([-1,-1,12,8]);
    $("tip").hidden = true;
    $("size").innerHTML = st.source === "text" ? "<b>Type something</b>" : "<b>Choose an SVG</b>";
    return;
  }
  const warped = warpSource(srcCache);
  if(!dragging || !view) view = computeView(warped);
  const {vb} = view, {toIn} = viewMap();
  const r = mat.getBoundingClientRect();
  const aspect = r.width && r.height ? r.width / r.height : 1.5;
  let [vx, vy, vw, vh] = vb;
  vh += vh * (r.width < 600 ? .3 : .12);
  if(vw/vh < aspect){ const nw = vh*aspect; vx -= (nw-vw)/2; vw = nw; }
  else { const nh = vw/aspect; vy -= (nh-vh)/2; vh = nh; }
  // camera: zoom around the fitted center, plus pan offset (inches)
  fitW = vw;
  if(cam.abs){ // user has zoomed or panned: keep the camera where they put it
    vw = cam.abs.w; vh = vw / aspect; vx = cam.abs.cx - vw/2; vy = cam.abs.cy - vh/2;
    cam.z = fitW / vw;
  } else cam.z = 1;
  drawMat([vx, vy, vw, vh]);

  const inUnits = warped.map(u => ({fill:u.fill, color: st.source === "text" ? st.textColor : u.color, contours: u.contours.map(c => c.map(p => toIn(p[0], p[1])))}));

  if(dragging){
    for(const u of inUnits){
      const d = u.contours.map(c => "M" + c.map(p => fmt(p[0]) + " " + fmt(p[1])).join("L") + "Z").join("");
      put(el("path", {d, fill:u.color || "var(--ws-accent)", "fill-rule":u.fill}));
    }
    drawHandles(vw);
    return;
  }

  // clean each piece, then weld within each color (first-seen color order keeps the stacking).
  // A piece with one outline only needs its direction fixed (no Clipper call); pieces with
  // holes or several outlines go through a union with their own fill rule. A color with only
  // one piece is already clean, so it skips the weld union too. This keeps 5,000-piece files fast.
  const tol = .0015;
  const cleaned = [];
  for(const u of inUnits){
    const rings = toClip(u.contours.map(c => simplifyRing(c, tol))).filter(p => p.length >= 3);
    let paths;
    if(rings.length === 1){
      const p = rings[0];
      if(Math.abs(ClipperLib.Clipper.Area(p)) < 1) paths = [];          // collapsed to nothing at this warp
      else { if(!ClipperLib.Clipper.Orientation(p)) p.reverse(); paths = [p]; }
    } else paths = union(rings, u.fill === "evenodd" ? ClipperLib.PolyFillType.pftEvenOdd : ClipperLib.PolyFillType.pftNonZero);
    if(paths.length) cleaned.push({color:u.color, paths, n:1});
  }
  let main;
  weldPartial = false; weldDeadline = dragging ? Infinity : performance.now() + WELD_BUDGET_MS;
  if(st.weld){
    const groups = new Map();
    for(const c of cleaned){
      const key = c.color || "";
      if(!groups.has(key)) groups.set(key, {paths:[], n:0});
      const g = groups.get(key); g.paths.push(...c.paths); g.n++;
    }
    main = [...groups].map(([key, g]) => ({color: key || null, paths: g.n > 1 ? unionMany(g.paths) : g.paths}));
  } else main = cleaned;
  main = main.filter(m => m.paths.length);
  const allMain = [].concat(...main.map(m => m.paths));
  // pre-welding everything first makes the offset much cheaper; it shares the weld time budget
  let off = st.offset > 0 && allMain.length ? offsetPaths(allMain, st.offset, st.join, !(st.weld && main.length === 1)) : null;
  if(off && !off.length) off = null;

  if(off) put(el("path", {d: clipToD(off,0,0), fill:"var(--ws-offset)", "fill-rule":"nonzero"}));
  // one <path> per color on screen (weld off draws a thin line round each piece instead)
  const show = new Map();
  for(const m of main){ const c = m.color || ""; show.set(c, (show.get(c) || "") + clipToD(m.paths,0,0)); }
  for(const [c, d] of show) put(el("path", {d, fill: c || "var(--ws-accent)", "fill-rule":"nonzero",
    stroke: st.weld ? "none" : "rgba(255,255,255,.8)", "stroke-width": st.weld ? 0 : 1, "vector-effect":"non-scaling-stroke"}));
  drawHandles(vw);

  const bounds = paths => { let a=1e9,b=1e9,c=-1e9,d=-1e9; for(const p of paths) for(const q of p){ const x=q.X/SC,y=q.Y/SC; if(x<a)a=x; if(y<b)b=y; if(x>c)c=x; if(y>d)d=y; } return [a,b,c,d]; };
  if(!allMain.length){
    $("size").innerHTML = "<b>Nothing to cut</b><br>This shape squashed the design flat. Ease the slider back or pick another shape.";
    return;
  }
  const B = bounds(off || allMain);
  lastOut = {main, off, B};
  const W = B[2]-B[0], H = B[3]-B[1];
  const colors = new Set(main.map(m => m.color || "")).size;
  $("size").innerHTML = `<b>${W.toFixed(2)} × ${H.toFixed(2)} in</b><br>${pieceCount(main)}${colors > 1 ? `, ${colors} colors` : ""}${off ? (offsetNote === "simplified" ? " plus offset (smoothed, very detailed design)" : " plus offset") : ""}${st.flipH ? ", mirrored" : ""}`
    + (st.offset > 0 && offsetNote === "too detailed" ? `<br>Too detailed for an offset outline. Try a simpler design or a bigger offset.` : "")
    + (weldPartial ? `<br>Very detailed design: some overlapping pieces weren't welded.` : "");
  $("download").disabled = !(W > 0);
}
function pieceCount(main){
  let n = 0;
  for(const m of main) for(const p of m.paths) if(ClipperLib.Clipper.Orientation(p)) n++;
  return n === 1 ? "1 piece" : n + " pieces";
}

function el(tag, attrs){
  const e = document.createElementNS(NS, tag);
  for(const k in attrs){ const v = attrs[k]; if((k === "fill" || k === "stroke") && typeof v === "string" && v.indexOf("var(") === 0) e.style.setProperty(k, v); else e.setAttribute(k, v); }
  return e;
}

function drawMat(vb){
  const [x,y,w,h] = vb;
  curVB = vb.slice();
  mat.setAttribute("viewBox", vb.map(fmt).join(" "));
  mat.setAttribute("preserveAspectRatio", "xMidYMid meet");
  const defs = el("defs", {});
  const minor = el("pattern", {id:"wsGMinor", width:.25, height:.25, patternUnits:"userSpaceOnUse"});
  minor.appendChild(el("path", {d:"M.25 0H0V.25", fill:"none", stroke:"var(--ws-grid)", "stroke-width":1, "vector-effect":"non-scaling-stroke"}));
  const major = el("pattern", {id:"wsGMajor", width:1, height:1, patternUnits:"userSpaceOnUse"});
  major.appendChild(el("rect", {width:1, height:1, fill:"url(#wsGMinor)"}));
  major.appendChild(el("path", {d:"M1 0H0V1", fill:"none", stroke:"var(--ws-grid-major)", "stroke-width":1, "vector-effect":"non-scaling-stroke"}));
  defs.append(minor, major);
  put(defs);
  put(el("rect", {x, y, width:w, height:h, fill:"var(--ws-mat)", class:"bg"}));
  put(el("rect", {x, y, width:w, height:h, fill:"url(#wsGMajor)", class:"bg"}));
  $("zPct").textContent = Math.round(cam.z*100) + "%";
}

/* ---------------- zoom and pan ---------------- */
const cam = {z:1, abs:null};
let curVB = null, camT = 0, fitW = 10;
function applyCam(nvb){ // fast path: move the viewBox now, full redraw (handle sizes) shortly after
  curVB = nvb;
  cam.abs = {cx: nvb[0] + nvb[2]/2, cy: nvb[1] + nvb[3]/2, w: nvb[2]};
  cam.z = fitW / nvb[2];
  mat.setAttribute("viewBox", nvb.map(fmt).join(" "));
  mat.querySelectorAll("rect.bg").forEach(r => { r.setAttribute("x", nvb[0]); r.setAttribute("y", nvb[1]); r.setAttribute("width", nvb[2]); r.setAttribute("height", nvb[3]); });
  $("zPct").textContent = Math.round(cam.z*100) + "%";
  clearTimeout(camT); camT = setTimeout(render, 140);
}
function zoomAt(f, clientX, clientY){
  if(!curVB) return;
  const z2 = clamp(cam.z * f, .2, 25); f = z2 / cam.z; if(Math.abs(f - 1) < 1e-6) return;
  const r = mat.getBoundingClientRect();
  const [vx, vy, vw, vh] = curVB;
  const fx = clientX === undefined ? .5 : (clientX - r.left) / r.width, fy = clientY === undefined ? .5 : (clientY - r.top) / r.height;
  const Px = vx + fx*vw, Py = vy + fy*vh, nw = vw/f, nh = vh/f, nx = Px - fx*nw, ny = Py - fy*nh;
  applyCam([nx, ny, nw, nh]);
}
function panBy(dxPx, dyPx){
  if(!curVB) return;
  const r = mat.getBoundingClientRect(), [vx, vy, vw, vh] = curVB;
  const ux = dxPx * vw / r.width, uy = dyPx * vh / r.height;
  applyCam([vx - ux, vy - uy, vw, vh]);
}
function fitCam(){ cam.abs = null; render(); }
mat.addEventListener("wheel", e => {
  e.preventDefault();
  // trackpad pinch arrives as ctrl+wheel; trackpad two-finger scroll has sideways movement; a mouse wheel zooms
  if(e.ctrlKey) zoomAt(Math.exp(-e.deltaY * .01), e.clientX, e.clientY);
  else if(e.deltaMode === 0 && e.deltaX !== 0) panBy(-e.deltaX, -e.deltaY);
  else zoomAt(Math.exp(-(e.deltaMode === 1 ? e.deltaY*33 : e.deltaY) * .0015), e.clientX, e.clientY);
}, {passive:false});
// drag empty mat to pan; two fingers to pinch-zoom
const touches = new Map(); let pinch = null, panning = false;
mat.addEventListener("pointerdown", e => {
  const t = e.target;
  if(t.dataset && (t.dataset.edge || t.dataset.guide)) return; // handles and edges take precedence
  touches.set(e.pointerId, {x:e.clientX, y:e.clientY});
  mat.setPointerCapture(e.pointerId);
  if(touches.size === 2){
    const [a, b] = [...touches.values()];
    pinch = {d: Math.hypot(a.x-b.x, a.y-b.y), mx:(a.x+b.x)/2, my:(a.y+b.y)/2};
    panning = false;
  } else if(touches.size === 1){ panning = true; mat.classList.add("panning"); }
});
mat.addEventListener("pointermove", e => {
  if(!touches.has(e.pointerId)) return;
  const prev = touches.get(e.pointerId), cur = {x:e.clientX, y:e.clientY};
  touches.set(e.pointerId, cur);
  if(pinch && touches.size === 2){
    const [a, b] = [...touches.values()];
    const d = Math.hypot(a.x-b.x, a.y-b.y), mx = (a.x+b.x)/2, my = (a.y+b.y)/2;
    panBy(mx - pinch.mx, my - pinch.my);
    if(pinch.d > 0) zoomAt(d / pinch.d, mx, my);
    pinch = {d, mx, my};
  } else if(panning) panBy(cur.x - prev.x, cur.y - prev.y);
});
const endPan = e => {
  if(!touches.has(e.pointerId)) return;
  touches.delete(e.pointerId);
  if(touches.size < 2) pinch = null;
  if(touches.size === 0){ panning = false; mat.classList.remove("panning"); }
};
mat.addEventListener("pointerup", endPan);
mat.addEventListener("pointercancel", endPan);
$("zIn").addEventListener("click", () => zoomAt(1.4));
$("zOut").addEventListener("click", () => zoomAt(1/1.4));
$("zFit").addEventListener("click", fitCam);
let overMat = false;
mat.addEventListener("pointerenter", () => { overMat = true; });
mat.addEventListener("pointerleave", () => { overMat = false; });
window.addEventListener("keydown", e => {
  if(!overMat || /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) return;
  if(e.key === "+" || e.key === "=") zoomAt(1.25);
  else if(e.key === "-") zoomAt(1/1.25);
  else if(e.key === "0") fitCam();
});

/* ---------------- handles ---------------- */
let sel = null, lastTap = null;
function drawHandles(vw){
  const tip = $("tip");
  if(st.type !== "envelope" && st.type !== "path"){ tip.hidden = true; return; }
  const {W, H} = srcCache, {toIn} = viewMap();
  const rad = vw / 70;
  const g = el("g", {});
  const guide = (pts, hitEdge) => {
    const d = "M" + pts.map(p => fmt(p[0])+" "+fmt(p[1])).join("L");
    g.appendChild(el("path", {d, fill:"none", stroke:"var(--ws-ink)", "stroke-width":1.5, "stroke-dasharray":"5 4", "vector-effect":"non-scaling-stroke", opacity:.85}));
    if(hitEdge){ const h = el("path", {d, fill:"none", stroke:"transparent", "stroke-width":20, "vector-effect":"non-scaling-stroke", style:"cursor:copy"}); h.dataset.guide = hitEdge; g.appendChild(h); }
  };
  const handles = [];
  if(st.type === "envelope"){
    for(const edge of ["top","bot","left","right"]){
      const P = envFull(edge), pts = [];
      for(let i=0;i<=160;i++){ const q = envAt(P, i/160); pts.push(toIn(q[0]*W, q[1]*H)); }
      guide(pts, edge);
    }
    for(const edge of ["top","bot","left","right"]) (st.env[edge] || []).forEach((p, i) => handles.push({edge, i, at: toIn(p[1]*W, p[2]*H)}));
    tip.textContent = "Drag dots to reshape. Tap any dashed edge to add a dot there, double-tap a dot to remove it.";
  } else {
    guide(pathCache.poly.map(p => toIn(p[0], p[1])), "path");
    st.path.forEach((p, i) => handles.push({edge:"path", i, at: toIn(p[0]*W, p[1]*W)}));
    tip.textContent = "Drag dots to reshape the line. Tap it to add a dot, double-tap a dot to remove it.";
  }
  tip.hidden = false;
  for(const h of handles){
    const on = sel && sel.edge === h.edge && sel.i === h.i;
    const c = el("circle", {cx:h.at[0], cy:h.at[1], r: on ? rad*1.25 : rad, fill: on ? "var(--ws-ink)" : "var(--ws-accent)", stroke: on ? "var(--ws-accent)" : "var(--ws-ink)", "stroke-width": on ? 3 : 2, "vector-effect":"non-scaling-stroke", style:"cursor:grab"});
    c.dataset.edge = h.edge; c.dataset.i = h.i;
    g.appendChild(c);
    const hit = el("circle", {cx:h.at[0], cy:h.at[1], r:rad*1.5, fill:"transparent", style:"cursor:grab"});
    hit.dataset.edge = h.edge; hit.dataset.i = h.i;
    g.appendChild(hit);
  }
  put(g);
}
function svgPoint(evt){
  const pt = mat.createSVGPoint(); pt.x = evt.clientX; pt.y = evt.clientY;
  return pt.matrixTransform(mat.getScreenCTM().inverse());
}
function insertEnvDot(edge, u){
  const full = envFull(edge);
  if(full.some(p => Math.abs(p[0]-u) < .02)) return -1;
  const q = envAt(full, u), arr = st.env[edge] || (st.env[edge] = []);
  let i = arr.findIndex(p => p[0] > u); if(i < 0) i = arr.length;
  arr.splice(i, 0, [u, q[0], q[1]]);
  return i;
}
function setAddEdge(edge){
  st.addEdge = edge;
  ROOT.querySelectorAll("[data-ae]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.ae === edge)));
}
function removeDot(s){
  if(!s) { status("Tap a dot first to select it.", 2500); return; }
  if(s.edge === "path"){
    if(st.path.length <= 2){ status("A line needs at least two dots.", 2500); return; }
    st.path.splice(s.i, 1);
  } else {
    const P = st.env[s.edge];
    if((s.edge === "top" || s.edge === "bot") && (s.i === 0 || s.i === P.length-1)){ status("Corner dots stay. Drag them instead.", 2500); return; }
    P.splice(s.i, 1);
  }
  sel = null; render();
}
mat.addEventListener("pointerdown", e => {
  const t = e.target; if(!t.dataset) return;
  if(t.dataset.guide && srcCache){
    e.preventDefault();
    const {W, H} = srcCache, pt = svgPoint(e), [lx, ly] = viewMap().fromIn(pt.x, pt.y);
    let idx = -1;
    if(t.dataset.guide === "path"){
      const poly = pathCache.poly; let best = 1e18, j = 0;
      poly.forEach((p, k) => { const d = (p[0]-lx)**2 + (p[1]-ly)**2; if(d < best){ best = d; j = k; } });
      const seg = Math.min(st.path.length-2, Math.floor(j / pathCache.per));
      idx = seg + 1; st.path.splice(idx, 0, [poly[j][0]/W, poly[j][1]/W]);
    } else {
      const P = envFull(t.dataset.guide); let best = 1e18, bu = .5;
      for(let k=0;k<=400;k++){ const u = k/400, q = envAt(P, u), d = ((q[0]*W-lx)**2 + (q[1]*H-ly)**2); if(d < best){ best = d; bu = u; } }
      idx = insertEnvDot(t.dataset.guide, bu);
    }
    if(idx < 0) return;
    sel = {edge: t.dataset.guide, i: idx};
    if(sel.edge !== "path") setAddEdge(sel.edge);
    dragging = {edge: sel.edge, i: idx, id: e.pointerId};
    mat.setPointerCapture(e.pointerId);
    render();
    return;
  }
  if(!t.dataset.edge) return;
  e.preventDefault();
  const edge = t.dataset.edge, i = +t.dataset.i, now = performance.now();
  if(lastTap && lastTap.edge === edge && lastTap.i === i && now - lastTap.t < 350){ lastTap = null; removeDot({edge, i}); return; }
  lastTap = {edge, i, t: now};
  sel = {edge, i};
  if(edge !== "path") setAddEdge(edge);
  dragging = {edge, i, id: e.pointerId};
  mat.setPointerCapture(e.pointerId);
  render();
});
let raf = 0;
mat.addEventListener("pointermove", e => {
  if(!dragging || e.pointerId !== dragging.id) return;
  const p = svgPoint(e), {W, H} = srcCache;
  const [lx, ly] = viewMap().fromIn(p.x, p.y);
  if(dragging.edge === "path") st.path[dragging.i] = [lx/W, ly/W];
  else { const P = st.env[dragging.edge]; P[dragging.i] = [P[dragging.i][0], lx/W, ly/H]; }
  if(!raf) raf = requestAnimationFrame(() => { raf = 0; render(); });
});
const endDrag = e => { if(!dragging) return; dragging = null; try{ mat.releasePointerCapture(e.pointerId); }catch(_){}; render(); };
mat.addEventListener("pointerup", endDrag);
mat.addEventListener("pointercancel", endDrag);

/* ---------------- download (shared popup: BT.runWithProgress in the core) ---------------- */
let startedSent = false;
function started(how){
  if(startedSent) return; startedSent = true;
  if(window.BT && window.BT.klaviyoEvent) window.BT.klaviyoEvent("Started " + TOOL_NAME, {source: how});
}
function buildSvg(){
  const {main, off, B} = lastOut;
  const [x0, y0, x1, y1] = B, W = x1-x0, H = y1-y0;
  const layers = [];
  if(off) layers.push(`<g id="offset" fill="#1F1A1C"><path d="${clipToD(off, x0, y0)}"/></g>`);
  if(!off || st.keepOrig){
    const byColor = new Map();
    for(const m of main){ const c = m.color || st.textColor; if(!byColor.has(c)) byColor.set(c, []); byColor.get(c).push(m.paths); }
    let n = 0;
    for(const [c, sets] of byColor){
      n++;
      const ds = sets.map(ps => clipToD(ps, x0, y0)).filter(Boolean);
      if(!ds.length){ n--; continue; }
      const paths = ds.map(d => `<path d="${d}"/>`).join("\n    ");
      layers.push(`<g id="${byColor.size > 1 ? "color-" + n : "design"}" fill="${c}">\n    ${paths}\n  </g>`);
    }
  }
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${fmt(W)}in" height="${fmt(H)}in" viewBox="0 0 ${fmt(W)} ${fmt(H)}">
  ${layers.join("\n  ")}
</svg>`;
}
function fileName(){
  const base = st.source === "text"
    ? (st.text.trim().split(/\s+/).slice(0, 4).join("-") || "text")
    : svgBaseName;
  const clean = base.normalize("NFC").replace(/[^\p{L}\p{N}_-]+/gu, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 60).toLowerCase();
  return (clean || "design") + "-warped.svg";
}
function saveFile(svg, name){
  if(window.BT && window.BT.trueSize) svg = window.BT.trueSize(svg); // 72 units/in for Design Space
  if(window.BT && window.BT.download){ window.BT.download(svg, name, "image/svg+xml"); return; }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([svg], {type:"image/svg+xml"}));
  a.download = (window.BT && BT.brandName ? BT.brandName(name) : name); document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

function runWithProgress(work){
  if(document.querySelector(".bt-proc")) return; // popup already open
  $("download").disabled = true;
  if(window.BT && window.BT.runWithProgress){
    window.BT.runWithProgress({
      stages: [{p:24, wait:350, text: MODE === "image" ? "Warping your design…" : "Warping your letters…"}, {p:58, wait:800, text:"Welding the pieces…"}, {p:84, wait:700, text:"Sizing it for your mat…"}],
      gateText: "Your file is almost ready! Enter your email to get it.",
      doneText: "Your file is ready!",
      work,
      onEnd: () => { $("download").disabled = !lastOut; }
    });
    return;
  }
  // old core cached: no popup, just save the file
  try{ work({gated:false}); }catch(e){ console.error("[" + TOOL_NAME + "]", e); }
  $("download").disabled = !lastOut;
}

$("download").addEventListener("click", () => {
  if(renderPending()) flushRender();
  if(!lastOut) return;
  const svg = buildSvg(), name = fileName();
  const props = {source: MODE === "image" ? (st.svgFrom || "upload") : "text", shape: st.preset, width_in: st.width, offset: st.offset > 0, mirrored: st.flipH};
  runWithProgress(() => {
    saveFile(svg, name);
    if(window.BT && window.BT.klaviyoEvent) window.BT.klaviyoEvent("Used " + TOOL_NAME, props);
  });
});

/* ---------------- UI wiring ---------------- */
function pressGroup(container, attr, value){
  container.querySelectorAll(`[${attr}]`).forEach(b => b.setAttribute("aria-pressed", String(b.getAttribute(attr) === value)));
}

// fonts list (text mode)
const fontsEl = $("fonts");
function drawFontList(){
  if(!fontsEl) return;
  fontsEl.innerHTML = "";
  const list = FONTS.map(f => ({name:f[0], tags:f[2], family:f[0]}));
  if(st.customFont) list.unshift({name:"__custom", label: st.customFontName, tags:"your upload", family:"inherit"});
  for(const f of list){
    const b = document.createElement("button");
    b.type = "button"; b.setAttribute("aria-pressed", String(st.font === f.name));
    const escH = v => String(v).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
    b.innerHTML = `<span class="fn" style="font-family:'${escH(f.family)}',cursive">${escH(f.label || f.name)}</span><span class="ft">${escH(f.tags)}</span>`;
    b.addEventListener("click", () => { st.font = f.name; drawFontList(); rebuildSource(); });
    fontsEl.appendChild(b);
  }
}

if(MODE === "text"){
  drawFontList();

  $("fontFile").addEventListener("change", async e => {
    const f = e.target.files[0]; if(!f) return;
    try{
      const font = opentype.parse(await f.arrayBuffer());
      st.customFont = font; st.customFontName = f.name.replace(/\.(ttf|otf)$/i, ""); st.font = "__custom";
      drawFontList(); rebuildSource();
    }catch(err){ status("That font file couldn't be read. Try a .ttf or .otf.", 5000); }
    e.target.value = "";
  });

  let textT = 0;
  $("text").addEventListener("input", e => { st.text = e.target.value; started("text"); clearTimeout(textT); textT = setTimeout(rebuildSource, 120); });
  $("letter").addEventListener("input", e => { st.letter = +e.target.value; $("letterOut").textContent = st.letter; rebuildSource(); });
  $("line").addEventListener("input", e => { st.line = +e.target.value; $("lineOut").textContent = st.line.toFixed(2); rebuildSource(); });
  ROOT.querySelectorAll("[data-align]").forEach(b => b.addEventListener("click", () => {
    st.align = b.dataset.align; pressGroup(b.parentElement, "data-align", st.align); rebuildSource();
  }));

  // font color
  const SWATCHES = [...new Set([st.textColor, "#E8537A","#F6A8BE","#2F8F6E","#8FD3B6","#2B6CB0","#8EC5F0","#7B5CD6","#B7A3E0","#F2785C","#F7B78D","#1F1A1C","#FFFFFF","#8B5A3C"].map(c => c.toUpperCase()))].slice(0, 14);
  const syncColor = () => {
    $("textColor").value = st.textColor.toLowerCase();
    $("swatches").querySelectorAll("button").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.c.toLowerCase() === st.textColor.toLowerCase())));
  };
  SWATCHES.forEach(c => {
    const b = document.createElement("button");
    b.dataset.c = c; b.style.background = c; b.title = c; b.setAttribute("aria-label", "Color " + c);
    b.type = "button";
    b.addEventListener("click", () => { st.textColor = c; syncColor(); requestRender(); });
    $("swatches").appendChild(b);
  });
  $("textColor").addEventListener("input", e => { st.textColor = e.target.value; syncColor(); requestRender(); });
  syncColor();
} else {
  // SVG design: file picker, drag-and-drop onto the page, and the built-in sample
  const plural = (n, w) => n + " " + w + (n === 1 ? "" : "s");
  const skippedNote = sk => {
    if(!sk) return "";
    const bits = [];
    if(sk.text) bits.push(plural(sk.text, "text item"));
    if(sk.outlines) bits.push(plural(sk.outlines, "outline-only shape"));
    if(sk.linked) bits.push(plural(sk.linked, "linked copy"));
    if(sk.images) bits.push(plural(sk.images, "picture"));
    if(sk.hidden) bits.push(plural(sk.hidden, "hidden shape"));
    return bits.length ? ` Skipped ${bits.join(", ")}.` + (sk.text || sk.outlines || sk.linked ? " Convert text and strokes to filled paths first to include them." : "") : "";
  };
  const useUnits = (units, name, from) => {
    if(!units.length){
      const sk = units.skipped || {};
      throw new Error(sk.outlines || sk.text || sk.linked
        ? "No filled shapes in that SVG. It only has outlines, text or linked copies. Convert them to filled paths, then try again."
        : "No filled shapes found in that SVG.");
    }
    st.svgUnits = units; st.svgFrom = from;
    svgBaseName = name;
    cam.abs = null;
    $("svgNote").textContent = from === "sample"
      ? `Showing a sample design (${units.length} shapes). Choose your own SVG to warp it.`
      : `${name}.svg: ${plural(units.length, "shape")} found.` + skippedNote(units.skipped);
    $("sample").hidden = from === "sample";
  };
  const MAX_MB = 30;
  let loadToken = 0;
  const loadSvgFile = async (f, how) => {
    if(!f) return;
    const token = ++loadToken;
    if(f.size > MAX_MB*1024*1024){ status(`That file is over ${MAX_MB} MB. Try a simpler SVG.`, 6000); return; }
    let txt = "";
    try{ txt = await f.text(); }catch(e){ status("Couldn't open that file.", 5000); return; }
    // accept by extension, type, or content (files saved without .svg still work)
    const looksSvg = /<svg[\s>]/i.test(txt.slice(0, 4096));
    if(!looksSvg){ status(/\.svg$/i.test(f.name) ? "That file isn't an SVG, or it's damaged. Try saving it again as SVG." : "That's not an SVG. Choose a .svg file.", 6000); return; }
    status(f.size > 300*1024 ? "Reading a big file… this can take a few seconds." : "Reading your file…");
    await new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));   // let the message paint first
    if(token !== loadToken) return;                                         // a newer file was picked meanwhile
    try{
      const units = loadSvgText(txt);
      if(token !== loadToken) return;
      useUnits(units, f.name.replace(/\.svg$/i, ""), "upload");
      track("file_upload", {file_type: "svg", file_kb: Math.round(f.size / 1024), shapes: units.length, method: how});
      started("svg");
    }catch(err){ status(err.message || "Couldn't read that SVG.", 6000); return; }
    await rebuildSource();
    if(token === loadToken && /^Reading/.test($("status").textContent)) status("");
  };
  const loadSample = () => {
    try{ useUnits(loadSvgText(SAMPLE_SVG), "sample-design", "sample"); }catch(e){ console.error(e); }
    rebuildSource();
  };
  $("svgFile").addEventListener("change", e => { loadSvgFile(e.target.files[0], "picker"); e.target.value = ""; });
  $("sample").addEventListener("click", () => { loadSample(); track("sample_click"); });
  // drop an SVG anywhere on the tool
  let dragDepth = 0;
  const hasFiles = e => e.dataTransfer && [...e.dataTransfer.types].includes("Files");
  ROOT.addEventListener("dragenter", e => { if(!hasFiles(e)) return; e.preventDefault(); dragDepth++; ROOT.classList.add("ws-dropping"); });
  ROOT.addEventListener("dragover", e => { if(!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; });
  ROOT.addEventListener("dragleave", () => { dragDepth = Math.max(0, dragDepth-1); if(!dragDepth) ROOT.classList.remove("ws-dropping"); });
  ROOT.addEventListener("drop", e => {
    if(!hasFiles(e)) return;
    e.preventDefault(); dragDepth = 0; ROOT.classList.remove("ws-dropping");
    loadSvgFile(e.dataTransfer.files[0], "drop");
  });
  loadSample();
}

// presets
const presetsEl = $("presets");
function thumb(p){
  const W = 3, H = 1;
  const svg = el("svg", {viewBox:"0 0 60 38", "aria-hidden":"true"});
  if(p.type === "envelope"){
    svg.innerHTML = `<path class="t-fill" d="M8 12 Q30 2 52 12 L52 28 Q30 36 8 28Z"/><path class="t-edge" stroke-width="1.3" d="M8 12 Q30 2 52 12 L52 28 Q30 36 8 28Z"/><g class="t-dot"><circle cx="8" cy="12" r="3"/><circle cx="30" cy="7" r="3"/><circle cx="52" cy="12" r="3"/><circle cx="8" cy="28" r="3"/><circle cx="30" cy="32" r="3"/><circle cx="52" cy="28" r="3"/></g>`;
    return svg;
  }
  if(p.type === "path"){
    svg.innerHTML = `<path class="t-line" d="M6 28 C18 4 38 36 54 10"/><g class="t-dot"><circle cx="6" cy="28" r="3"/><circle cx="54" cy="10" r="3"/></g>`;
    return svg;
  }
  const save = {type:st.type, amount:st.amount, freq:st.freq};
  st.type = p.type; st.amount = p.amount; st.freq = p.freq || 1;
  const lines = [];
  for(let r=0;r<=4;r++){ const pts=[]; for(let i=0;i<=40;i++) pts.push(warp(i/40, r/4, W, H)); lines.push(pts); }
  for(let c=0;c<=6;c++){ const pts=[]; for(let i=0;i<=10;i++) pts.push(warp(c/6, i/10, W, H)); lines.push(pts); }
  Object.assign(st, save);
  let a=1e9,b=1e9,c=-1e9,d=-1e9; for(const l of lines) for(const q of l){ a=Math.min(a,q[0]); b=Math.min(b,q[1]); c=Math.max(c,q[0]); d=Math.max(d,q[1]); }
  const s = Math.min(52/(c-a), 30/(d-b)), mx = 30 - (a+c)/2*s, my = 19 - (b+d)/2*s;
  svg.appendChild(el("path", {d:"M"+lines[0].concat([...lines[4]].reverse()).map(q=>fmt(q[0]*s+mx)+" "+fmt(q[1]*s+my)).join("L")+"Z", class:"t-fill"}));
  lines.forEach((l, i) => svg.appendChild(el("path", {d:"M"+l.map(q=>fmt(q[0]*s+mx)+" "+fmt(q[1]*s+my)).join("L"), class: (i===0||i===4) ? "t-edge" : "t-grid", "stroke-width": (i===0||i===4) ? 1.3 : .6})));
  return svg;
}
PRESETS.forEach(p => {
  const b = document.createElement("button");
  b.type = "button"; b.dataset.preset = p.id; b.setAttribute("aria-pressed", String(st.preset === p.id));
  b.appendChild(thumb(p));
  const sp = document.createElement("span"); sp.textContent = p.name; b.appendChild(sp);
  b.addEventListener("click", () => {
    selectPreset(p.id); track("shape_selected", {shape: p.id}); started("shape");
    // Image mode: most people try shapes on the sample and leave. Point them at their own file.
    if (MODE === "image" && window.BT && BT.nudge) BT.nudge("own-svg", {
      text: "Like that shape? Add your own SVG to bend it the same way.",
      action: "Add my SVG", delay: 1500,
      when: () => st.svgFrom !== "upload",
      onAction: () => { BT.openStep($("svgFile")); $("svgFile").click(); }
    });
  });
  presetsEl.appendChild(b);
});

function selectPreset(id, keepLm){
  const p = PRESETS.find(x => x.id === id);
  st.preset = id; st.type = p.type; st.amount = p.amount; st.freq = p.freq || 1;
  if(!keepLm) st.lm = p.lm || "stretch";
  sel = null;
  if(p.type === "envelope" && !st.env) st.env = defaultEnv();
  if(p.type === "path" && !st.path) st.path = defaultPath();
  pressGroup(presetsEl, "data-preset", id);
  syncAdjust();
  requestRender();
}
function syncAdjust(){
  const t = st.type;
  const hasAmount = !["none","envelope","path"].includes(t);
  $("amountRow").hidden = !hasAmount;
  $("amountLabel").textContent = AMOUNT_LABEL[t] || "Bend";
  $("amount").value = Math.round(st.amount*100);
  $("amountOut").textContent = t === "arc" ? Math.round(Math.abs(st.amount)*355) + "°" : Math.round(st.amount*100);
  $("freqRow").hidden = !(t === "flag" || t === "wave");
  $("freq").value = st.freq; $("freqOut").textContent = st.freq;
  $("customizeRow").hidden = !hasAmount;
  $("envRow").hidden = t !== "envelope";
  $("pathRow").hidden = t !== "path";
  pressGroup($("letterMode"), "data-lm", st.lm);
  syncRanges();
}
function syncRanges(){ if(window.BT && window.BT.syncRange) ROOT.querySelectorAll(".bt-range").forEach(r => window.BT.syncRange(r)); }
$("amount").addEventListener("input", e => { st.amount = +e.target.value/100; syncAdjust(); requestRender(); });
$("freq").addEventListener("input", e => { st.freq = +e.target.value; syncAdjust(); requestRender(); });
ROOT.querySelectorAll("[data-lm]").forEach(b => b.addEventListener("click", () => { st.lm = b.dataset.lm; syncAdjust(); requestRender(); }));

$("customize").addEventListener("click", () => {
  if(!srcCache) return;
  const {W, H} = srcCache;
  if(st.type === "arc" && Math.abs(st.amount) > .4) status("Tight circles don't convert well to handles, so this is an approximation.", 4000);
  const top = [], bot = [], N = 7;
  for(let i=0;i<N;i++){ const u = i/(N-1), a = warp(u, 0, W, H), b = warp(u, 1, W, H); top.push([u, a[0]/W, a[1]/H]); bot.push([u, b[0]/W, b[1]/H]); }
  const side = u => { // add a middle dot only if the preset bends that side
    const m = warp(u, .5, W, H), a = warp(u, 0, W, H), b = warp(u, 1, W, H);
    return Math.hypot(m[0] - (a[0]+b[0])/2, m[1] - (a[1]+b[1])/2) > .01*H ? [[.5, m[0]/W, m[1]/H]] : [];
  };
  const left = side(0), right = side(1);
  sel = null;
  st.env = {top, bot, left, right};
  const lm = st.lm;
  selectPreset("env", true); st.lm = lm; syncAdjust(); render();
});
$("envReset").addEventListener("click", () => { st.env = defaultEnv(); sel = null; render(); });
$("envMirror").addEventListener("click", () => {
  const E = st.env;
  const mirTB = A => { const up = A.filter(p => p[0] < .5 - 1e-6), mid = A.find(p => Math.abs(p[0]-.5) < 1e-6);
    const out = [...up]; if(mid) out.push([.5, mid[1], 0]); for(let i=up.length-1;i>=0;i--) out.push([1-up[i][0], up[i][1], -up[i][2]]); return out; };
  E.bot = E.top.map(p => [p[0], p[1], -p[2]]);
  E.left = mirTB(E.left || []); E.right = mirTB(E.right || []);
  sel = null; render();
});
$("envMirrorLR").addEventListener("click", () => {
  const E = st.env;
  const mirLR = A => { const lf = A.filter(p => p[0] < .5 - 1e-6), mid = A.find(p => Math.abs(p[0]-.5) < 1e-6);
    const out = [...lf]; if(mid) out.push([.5, 0, mid[2]]); for(let i=lf.length-1;i>=0;i--) out.push([1-lf[i][0], -lf[i][1], lf[i][2]]); return out; };
  E.top = mirLR(E.top); E.bot = mirLR(E.bot);
  E.right = (E.left || []).map(p => [p[0], -p[1], p[2]]);
  sel = null; render();
});
ROOT.querySelectorAll("[data-pp]").forEach(b => b.addEventListener("click", () => { st.pathPos = b.dataset.pp; pressGroup(b.parentElement, "data-pp", st.pathPos); render(); }));
$("pathStretch").addEventListener("change", e => { st.pathStretch = e.target.checked; render(); });
$("ptReset").addEventListener("click", () => { st.path = defaultPath(); sel = null; render(); });

const W_MIN = .25, W_MAX = 60;
$("width").min = W_MIN; $("width").max = W_MAX;
$("width").addEventListener("input", e => { const v = parseFloat(e.target.value); if(v >= W_MIN && v <= W_MAX){ st.width = v; requestRender(); } });
// on commit (Enter / leaving the box) snap the box to what will actually be used
$("width").addEventListener("change", e => {
  const v = parseFloat(e.target.value);
  if(isFinite(v) && v > 0){
    const c = clamp(v, W_MIN, W_MAX);
    if(c !== v) status(`Width is ${W_MIN}–${W_MAX} inches, so we used ${c}.`, 3500);
    st.width = c;
  }
  e.target.value = +st.width.toFixed(3);
  requestRender();
});
$("weld").addEventListener("change", e => { st.weld = e.target.checked; requestRender(); });
$("flipH").addEventListener("change", e => { st.flipH = e.target.checked; requestRender(); });

function resetAll(){
  Object.assign(st, {letter:0, line:.9, align:"center", env:null, path:null, pathPos:"on", pathStretch:false,
    width:8, weld:true, offset:0, join:"round", keepOrig:true, flipH:false});
  sel = null; lastTap = null; cam.abs = null;
  if(MODE === "text"){
    $("letter").value = 0; $("letterOut").textContent = "0";
    $("line").value = .9; $("lineOut").textContent = "0.90";
    ROOT.querySelectorAll("[data-align]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.align === "center")));
  }
  ROOT.querySelectorAll("[data-pp]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.pp === "on")));
  ROOT.querySelectorAll("[data-join]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.join === "round")));
  $("pathStretch").checked = false; $("width").value = 8; $("weld").checked = true; $("flipH").checked = false;
  $("offset").value = 0; $("offsetOut").textContent = "none"; $("offsetOpts").hidden = true; $("keepOrig").checked = true;
  selectPreset("none");
  syncRanges();
  rebuildSource();
  status("Reset to straight", 1800);
}
$("reset").addEventListener("click", resetAll);
$("offset").addEventListener("input", e => {
  st.offset = +e.target.value;
  $("offsetOut").textContent = st.offset > 0 ? st.offset.toFixed(3).replace(/0+$/,"").replace(/\.$/,"") + " in" : "none";
  $("offsetOpts").hidden = !(st.offset > 0);
  requestRender();
});
ROOT.querySelectorAll("[data-join]").forEach(b => b.addEventListener("click", () => { st.join = b.dataset.join; pressGroup(b.parentElement, "data-join", st.join); requestRender(); }));
$("keepOrig").addEventListener("change", e => { st.keepOrig = e.target.checked; });

let rT = 0;
window.addEventListener("resize", () => { clearTimeout(rT); rT = setTimeout(render, 80); });

selectPreset(START_PRESET);
if(MODE === "text") rebuildSource();
})();
